export type DraftStatus = 'initial' | 'restored' | 'saved' | 'error';
export interface DraftSnapshot<T> {
  readonly value: T;
  readonly status: DraftStatus;
  readonly savedAt: number | null;
  readonly error: string | null;
}
export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
export type DraftUpdater<T> = T | ((current: T) => T);
export interface DraftStore<T> {
  getSnapshot(): DraftSnapshot<T>;
  subscribe(listener: () => void): () => void;
  setValue(updater: DraftUpdater<T>): void;
  retry(): void;
}

const MAX_BYTES = 64 * 1024;
const encoder = new TextEncoder();
class DraftValidationError extends Error {}
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof DraftValidationError ? error.message : fallback;
}
function freeze<T>(
  value: T,
  status: DraftStatus,
  savedAt: number | null,
  error: string | null,
): DraftSnapshot<T> {
  return Object.freeze({ value, status, savedAt, error });
}

export function createDraftStore<T>({
  key,
  initialValue,
  decode,
  storage,
  now = Date.now,
}: {
  key: string;
  initialValue: T;
  decode: (value: unknown) => T;
  storage: DraftStorage;
  now?: () => number;
}): DraftStore<T> {
  const listeners = new Set<() => void>();
  let snapshot: DraftSnapshot<T> = freeze(initialValue, 'initial', null, null);
  try {
    const raw = storage.getItem(key);
    if (raw !== null) {
      if (encoder.encode(raw).byteLength > MAX_BYTES)
        throw new DraftValidationError('Черновик превышает 64 КБ.');
      const record: unknown = JSON.parse(raw);
      if (!record || typeof record !== 'object' || Array.isArray(record))
        throw new DraftValidationError('Формат черновика повреждён.');
      const envelope = record as {
        version?: unknown;
        savedAt?: unknown;
        value?: unknown;
      };
      if (
        envelope.version !== 1 ||
        typeof envelope.savedAt !== 'number' ||
        !Number.isFinite(envelope.savedAt) ||
        Math.abs(envelope.savedAt) > 8.64e15 ||
        !Object.hasOwn(envelope, 'value')
      )
        throw new DraftValidationError('Версия или поля черновика не поддерживаются.');
      let decoded: T;
      try {
        decoded = decode(envelope.value);
      } catch {
        throw new DraftValidationError('Данные черновика не прошли проверку.');
      }
      snapshot = freeze(decoded, 'restored', envelope.savedAt, null);
    }
  } catch (error) {
    snapshot = freeze(initialValue, 'error', null, errorMessage(error, 'Не удалось прочитать черновик.'));
  }
  const publish = (next: DraftSnapshot<T>) => {
    snapshot = next;
    for (const listener of [...listeners]) listener();
  };
  const persist = (value: T) => {
    let savedAt: number;
    try {
      savedAt = now();
      if (!Number.isFinite(savedAt) || Math.abs(savedAt) > 8.64e15)
        throw new DraftValidationError('Не удалось определить время сохранения.');
      const serialized = JSON.stringify({ version: 1, savedAt, value });
      if (typeof serialized !== 'string' || encoder.encode(serialized).byteLength > MAX_BYTES)
        throw new DraftValidationError('Черновик превышает 64 КБ.');
      storage.setItem(key, serialized);
      publish(freeze(value, 'saved', savedAt, null));
    } catch (error) {
      publish(
        freeze(value, 'error', snapshot.savedAt, errorMessage(error, 'Не удалось сохранить черновик.')),
      );
    }
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setValue(updater) {
      let next: T;
      try {
        next = typeof updater === 'function' ? (updater as (current: T) => T)(snapshot.value) : updater;
      } catch (error) {
        publish(
          freeze(
            snapshot.value,
            'error',
            snapshot.savedAt,
            errorMessage(error, 'Не удалось обновить черновик.'),
          ),
        );
        return;
      }
      persist(next);
    },
    retry() {
      persist(snapshot.value);
    },
  };
}
